/**
 * Centralized line item calculation utilities
 * Ensures consistent calculation logic across invoice, sales order, and estimate components
 */

export interface LineItemCalculationInput {
  quantity: number;
  unit_price: number;
  discount_percent?: number;
  tax_rate?: number;
  // Pre-calculated values from backend (if available)
  line_total?: number;
  discount_amount?: number;
  taxable_amount?: number;
  tax_amount?: number;
}

export interface LineItemCalculationResult {
  lineTotal: number;
  discountAmount: number;
  taxableValue: number;
  taxAmount: number;
  finalAmount: number;
}

/**
 * Calculate line item amounts with fallback to manual calculation
 * Prioritizes backend-calculated values when available
 */
export const calculateLineItemAmounts = (item: LineItemCalculationInput): LineItemCalculationResult => {
  // Use backend values if available, otherwise calculate
  const lineTotal = item.line_total ?? (item.quantity * item.unit_price);
  const discountAmount = item.discount_amount ?? (lineTotal * ((item.discount_percent || 0) / 100));
  const taxableValue = item.taxable_amount ?? (lineTotal - discountAmount);
  const taxAmount = item.tax_amount ?? (taxableValue * ((item.tax_rate || 0) / 100));
  const finalAmount = taxableValue + taxAmount;

  return {
    lineTotal,
    discountAmount,
    taxableValue,
    taxAmount,
    finalAmount,
  };
};

/**
 * Calculate totals for multiple line items
 */
export const calculateLineItemsTotals = (items: LineItemCalculationInput[]) => {
  const totals = items.reduce(
    (acc, item) => {
      const amounts = calculateLineItemAmounts(item);
      return {
        totalLineTotal: acc.totalLineTotal + amounts.lineTotal,
        totalDiscount: acc.totalDiscount + amounts.discountAmount,
        totalTaxable: acc.totalTaxable + amounts.taxableValue,
        totalTax: acc.totalTax + amounts.taxAmount,
        totalAmount: acc.totalAmount + amounts.finalAmount,
      };
    },
    {
      totalLineTotal: 0,
      totalDiscount: 0,
      totalTaxable: 0,
      totalTax: 0,
      totalAmount: 0,
    }
  );

  return totals;
};
